import { useState, useCallback } from 'react'
import { Box, Paper, Text } from '@mantine/core'
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion'
import type { WordFlashcard } from '../../api/vocabulary'

interface FlashCardProps {
  word: WordFlashcard
  onSwipeRight: () => void
  onSwipeLeft: () => void
  onSwipeUp: () => void
  onSwipeDown: () => void
  showBackFirst?: boolean
  blankWordInContext?: boolean
}

type OverlayLabel = 'KNOWN' | 'UNKNOWN' | 'FAMILIAR' | 'NEXT' | null

const SWIPE_THRESHOLD = 100
const EXIT_DISTANCE = 600

const OVERLAY_CONFIG: Record<string, { color: string; bg: string }> = {
  KNOWN:    { color: 'var(--sat-green)', bg: 'var(--sat-green-soft-strong)' },
  UNKNOWN:  { color: 'var(--sat-red)',   bg: 'var(--sat-red-soft-strong)' },
  FAMILIAR: { color: 'var(--sat-yellow)',bg: 'var(--sat-yellow-soft)' },
  NEXT:     { color: 'var(--sat-text-muted)', bg: 'var(--sat-neutral-soft)' },
}

export function FlashCard({
  word, onSwipeRight, onSwipeLeft, onSwipeUp, onSwipeDown,
  showBackFirst = false, blankWordInContext = false,
}: FlashCardProps) {
  const [isFlipped, setIsFlipped] = useState(showBackFirst)
  const [exiting, setExiting] = useState(false)
  const [exitDirection, setExitDirection] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const rotate = useTransform(x, [-300, 0, 300], [-15, 0, 15])
  const [overlayLabel, setOverlayLabel] = useState<OverlayLabel>(null)

  const handleFlip = useCallback(() => { if (!exiting) setIsFlipped((p) => !p) }, [exiting])

  const handleDrag = (_: any, info: { offset: { x: number; y: number } }) => {
    const { x: ox, y: oy } = info.offset
    const absX = Math.abs(ox), absY = Math.abs(oy)
    if (absX > absY && absX > 40) setOverlayLabel(ox > 0 ? 'KNOWN' : 'UNKNOWN')
    else if (absY > absX && absY > 40) setOverlayLabel(oy < 0 ? 'FAMILIAR' : 'NEXT')
    else setOverlayLabel(null)
  }

  const handleDragEnd = (_: any, info: { offset: { x: number; y: number } }) => {
    const { x: ox, y: oy } = info.offset
    const absX = Math.abs(ox), absY = Math.abs(oy)
    let triggered = false
    if (absX > absY && absX > SWIPE_THRESHOLD) {
      triggered = true
      setExitDirection({ x: ox > 0 ? EXIT_DISTANCE : -EXIT_DISTANCE, y: 0 })
      setExiting(true)
      setTimeout(() => (ox > 0 ? onSwipeRight : onSwipeLeft)(), 250)
    } else if (absY > absX && absY > SWIPE_THRESHOLD) {
      triggered = true
      setExitDirection({ x: 0, y: oy < 0 ? -EXIT_DISTANCE : EXIT_DISTANCE })
      setExiting(true)
      setTimeout(() => (oy < 0 ? onSwipeUp : onSwipeDown)(), 250)
    }
    if (!triggered) setOverlayLabel(null)
  }

  const blankWord = (s: string) =>
    blankWordInContext ? s.replace(new RegExp(`\\b${word.word}\\b`, 'gi'), '_____________') : s

  const highlightWord = (sentence: string) => {
    if (blankWordInContext) return blankWord(sentence)
    const parts = sentence.split(new RegExp(`(\\b${word.word}\\b)`, 'gi'))
    return parts.map((p) =>
      p.toLowerCase() === word.word.toLowerCase()
        ? `<strong style="color:var(--sat-text)">${p}</strong>`
        : p
    ).join('')
  }

  const overlay = overlayLabel ? OVERLAY_CONFIG[overlayLabel] : null
  const antonymColor = 'var(--mantine-color-orange-6)'
  const antonymBackground = 'var(--mantine-color-orange-light)'
  const synonymColor = 'var(--mantine-color-green-7)'
  const synonymBackground = 'var(--mantine-color-green-light)'

  const cardStyle: React.CSSProperties = {
    position: 'absolute', inset: 0, backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
    background: 'var(--sat-surface)',
    border: '1px solid var(--sat-border)',
    borderRadius: 12,
    boxShadow: '0 4px 20px var(--sat-shadow)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: 28, gap: 16, overflow: 'hidden',
  }

  const frontFaceStyle: React.CSSProperties = {
    ...cardStyle,
    opacity: isFlipped ? 0 : 1,
    visibility: isFlipped ? 'hidden' : 'visible',
  }

  const backFaceStyle: React.CSSProperties = {
    ...cardStyle,
    transform: 'rotateY(180deg)',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    overflowY: 'auto',
    paddingBottom: 44,
    opacity: isFlipped ? 1 : 0,
    visibility: isFlipped ? 'visible' : 'hidden',
  }

  return (
    <AnimatePresence>
      {!exiting ? (
        <motion.div
          key={word.id}
          style={{ x, y, rotate, width: 350, height: 500, perspective: 1000, cursor: 'grab', position: 'relative', userSelect: 'none' }}
          drag dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
          dragElastic={0.8} onDrag={handleDrag} onDragEnd={handleDragEnd}
          onClick={handleFlip} whileDrag={{ cursor: 'grabbing' }}
        >
          {overlay && (
            <Box style={{ position: 'absolute', inset: 0, borderRadius: 12, background: overlay.bg, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <span style={{
                fontSize: 32, fontWeight: 800, color: overlay.color, letterSpacing: 4,
                textTransform: 'uppercase', border: `3px solid ${overlay.color}`,
                borderRadius: 8, padding: '8px 24px',
                transform: overlayLabel === 'KNOWN' ? 'rotate(-15deg)' : overlayLabel === 'UNKNOWN' ? 'rotate(15deg)' : 'none',
              }}>
                {overlayLabel}
              </span>
            </Box>
          )}

          <Box style={{ width: '100%', height: '100%', position: 'relative', transformStyle: 'preserve-3d', transition: 'transform 0.5s ease', transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}>
            {/* FRONT */}
            <Paper style={frontFaceStyle}>
              <Text style={{ fontSize: 41.6, fontWeight: 700, color: 'var(--sat-text)', textAlign: 'center', lineHeight: 1.15 }}>{word.word}</Text>
              {word.root && <Text style={{ fontSize: 18.2, color: 'var(--sat-green)', fontStyle: 'italic', textAlign: 'center' }}>Root: {word.root}</Text>}
              {word.base64_image && <img src={`data:image/png;base64,${word.base64_image}`} alt={word.word} style={{ maxWidth: 200, maxHeight: 140, borderRadius: 8, objectFit: 'cover' }} />}
              {word.memory_anchor && (
                <Box style={{ fontSize: 19.5, color: 'var(--sat-green)', textAlign: 'center', background: 'var(--sat-surface-dim)', borderRadius: 8, padding: '10px 16px', lineHeight: 1.5 }}>
                  {word.memory_anchor}
                </Box>
              )}
              <Text style={{ position: 'absolute', bottom: 16, fontSize: 14.3, color: 'var(--sat-text-muted)', textAlign: 'center' }}>
                Click to flip &bull; Swipe to respond
              </Text>
            </Paper>

            {/* BACK */}
            <Paper style={backFaceStyle}>
              <Text style={{ fontSize: 22.1, color: 'var(--sat-green)', lineHeight: 1.5, fontWeight: 500 }}>{word.meaning}</Text>
              {word.memory_hint && <Text style={{ fontSize: 16.9, color: 'var(--sat-text-dim)', fontStyle: 'italic', lineHeight: 1.45 }}>{word.memory_hint}</Text>}
              <hr style={{ border: 'none', borderTop: '1px solid var(--sat-blush)', margin: '4px 0', width: '100%' }} />
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {word.antonyms.map((w) => <span key={`ant-${w}`} style={{ background: antonymBackground, color: antonymColor, border: `1px solid ${antonymColor}`, fontSize: 14.3, padding: '4px 11px', borderRadius: 12, fontWeight: 600 }}>{w}</span>)}
                {word.synonyms.map((w) => <span key={`syn-${w}`} style={{ background: synonymBackground, color: synonymColor, border: `1px solid ${synonymColor}`, fontSize: 14.3, padding: '4px 11px', borderRadius: 12, fontWeight: 600 }}>{w}</span>)}
                {word.family_words.map((w) => <span key={`fam-${w}`} style={{ background: 'var(--mantine-color-blue-light)', color: 'var(--mantine-color-blue-7)', border: '1px solid var(--mantine-color-blue-6)', fontSize: 14.3, padding: '4px 11px', borderRadius: 12, fontWeight: 600 }}>{w}</span>)}
              </Box>
              {word.context_sentences.length > 0 && (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4, width: '100%' }}>
                  {word.context_sentences.map((sentence, i) => (
                    <Box key={i} style={{ background: 'var(--sat-surface-dim)', borderRadius: 8, padding: '10px 14px', fontSize: 16.9, color: 'var(--sat-text)', lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: highlightWord(sentence) }} />
                  ))}
                </Box>
              )}
              <Text style={{ position: 'absolute', left: 28, right: 28, bottom: 2, fontSize: 14.3, color: 'var(--sat-text-muted)', textAlign: 'center' }}>
                Click to flip &bull; Swipe to respond
              </Text>
            </Paper>
          </Box>
        </motion.div>
      ) : (
        <motion.div
          key={`exit-${word.id}`}
          initial={{ x: 0, y: 0, opacity: 1 }}
          animate={{ x: exitDirection.x, y: exitDirection.y, opacity: 0, rotate: exitDirection.x > 0 ? 30 : exitDirection.x < 0 ? -30 : 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          style={{ width: 350, height: 500, background: 'var(--sat-surface)', borderRadius: 12, boxShadow: '0 4px 20px var(--sat-shadow)', position: 'absolute' }}
        />
      )}
    </AnimatePresence>
  )
}
